import * as yup from 'yup';
import { nameRegEx } from './constants';
import { validateEmail } from './emailHelper';
import ResourcesIds from '../resources/ResourcesIds';

/**
 * What every schema factory takes. Declared once here rather than repeated at the top of
 * each `*Schema.ts`.
 */
export type TranslateFn = (key: string, options?: any) => string;

/** Mirrors users.username VARCHAR(50), which the API validates against too. */
export const EMAIL_MAX: number = 50;
export const NAME_MAX: number = 100;
/**
 * An invited person's name, which is shorter than a patient's on an evaluation because it
 * mirrors `users.first_name` / `users.last_name` VARCHAR(50) rather than the evaluation's
 * own snapshot columns. Declared once here: three copies of one column width is exactly
 * the drift a shared bound exists to stop.
 */
export const INVITED_NAME_MAX: number = 50;
export const TEXT_MAX: number = 255;

/**
 * A pure predicate from `src/library/models/` as a Yup rule.
 *
 * Those functions are the API's own rules ported once, and several are already unit
 * tested. Wrapping them is what keeps a field's message and its form's Save button
 * answering to exactly one statement of the rule, rather than to two that can drift.
 */
export const predicateRule = (
    name: string,
    isValid: (value: string) => boolean,
    message: string
): yup.StringSchema<string> =>
    yup.string().defined().test(name, message, (value) => isValid(value ?? ''));

export const requiredText = (
    translate: TranslateFn,
    requiredKey: string,
    max: number = TEXT_MAX
): yup.StringSchema<string> =>
    yup.string().trim()
        .required(translate(requiredKey))
        .max(max, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max }));

export const optionalText = (
    translate: TranslateFn,
    max: number = TEXT_MAX
): yup.StringSchema<string> =>
    yup.string().defined()
        .max(max, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max }));

/**
 * An email address. `allowAdminHandle` lets the bare word "admin" through and is the
 * login form's case, and only the login form's case.
 */
export const emailField = (
    translate: TranslateFn,
    { required = true, allowAdminHandle = false }: { required?: boolean; allowAdminHandle?: boolean } = {}
): yup.StringSchema<string> => {
    const base: yup.StringSchema<string> = yup.string().trim().defined()
        .max(EMAIL_MAX, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max: EMAIL_MAX }))
        .test('email', translate(ResourcesIds.ENTER_VALID_EMAIL),
            (value) => !value?.trim() || validateEmail(value, allowAdminHandle));

    return required ? base.required(translate(ResourcesIds.EMAIL_REQUIRED)) : base;
};

/** A person's name: required, capped, and letters rather than a phone number. */
export const personName = (
    translate: TranslateFn,
    requiredKey: string,
    invalidKey: string
): yup.StringSchema<string> =>
    requiredText(translate, requiredKey, NAME_MAX)
        .matches(nameRegEx, translate(invalidKey));

export const password = (translate: TranslateFn, min: number = 6): yup.StringSchema<string> =>
    yup.string()
        .required(translate(ResourcesIds.PASSWORD_REQUIRED))
        .min(min, translate(ResourcesIds.INVITATION_PASSWORD_TOO_SHORT, { min }));

/**
 * The second of a password pair.
 *
 * A `function` rather than an arrow: `this.parent` is how yup reaches the sibling field,
 * and an arrow has no `this` to bind.
 */
export const confirmPassword = (
    translate: TranslateFn,
    siblingName: string = 'password'
): yup.StringSchema<string> =>
    yup.string()
        .required(translate(ResourcesIds.PASSWORD_REQUIRED))
        .test('matches', translate(ResourcesIds.PASSWORDS_DONT_MATCH), function (value): boolean {
            return value === (this.parent as Record<string, unknown>)[siblingName];
        });

/**
 * A phone number, loosely. Deliberately permissive -- the same rule `isValidWhatsAppNumber`
 * uses, because a clinic writes numbers down the way its patients say them, and a form that
 * refuses a valid Romanian mobile over a space is worse than one that lets a typo through
 * to a human who will notice.
 */
const PHONE_PATTERN: RegExp = /^\+?[0-9][0-9 ()\-]{5,28}$/;

export const phoneNumber = (
    translate: TranslateFn,
    invalidKey: string,
    required: boolean = false
): yup.StringSchema<string> =>
    yup.string().defined()
        .test('phone', translate(invalidKey),
            (value) => (!required && !value?.trim()) || PHONE_PATTERN.test((value ?? '').trim()));

/** The same shape `library/dateTimeHelper` round-trips and the API stores. */
const TIME_PATTERN: RegExp = /^([01]\d|2[0-3]):[0-5]\d$/;

export const timeOfDay = (
    translate: TranslateFn,
    invalidKey: string,
    required: boolean = true
): yup.StringSchema<string> =>
    yup.string().defined()
        .test('hh-mm', translate(invalidKey),
            (value) => (!required && !value?.trim()) || TIME_PATTERN.test(value ?? ''));

/**
 * The bounds of a number field, stated once so the browser's own spinner and the Yup rule
 * cannot drift apart. `numericSlotProps` feeds the input; `integerIn` feeds the schema.
 */
export interface NumericBounds {
    min: number;
    max: number;
    step?: number;
}

/** A patient's age. One is the floor: an age of nought is a typo, not an infant patient. */
export const AGE_BOUNDS: NumericBounds = { min: 1, max: 130 };

/**
 * The age of a child whose consent a legal representative is signing. Nought is real
 * here -- an infant brought in by a parent -- and seventeen is the last age that needs
 * somebody else to sign at all.
 */
export const CHILD_AGE_BOUNDS: NumericBounds = { min: 0, max: 17 };

export const PAIN_LEVEL_BOUNDS: NumericBounds = { min: 0, max: 10 };

/**
 * What a therapist can take off a service, as a percentage.
 *
 * Nought is a real answer -- most sales carry no discount at all -- so the floor is there to
 * refuse a *negative* one, which is a surcharge dressed as a reduction. The ceiling is where a
 * discount stops being a discount: more than everything off is a price the clinic pays the
 * patient. The API re-checks both, and the form's own input carries them through
 * `numericSlotProps` so the spinner cannot offer what the schema will refuse.
 */
export const DISCOUNT_PERCENT_BOUNDS: NumericBounds = { min: 0, max: 100 };

/**
 * The length of a booking typed in by hand, for the case where neither a service nor a plan
 * says how long it runs. Five minutes is the shortest the grid can draw.
 *
 * A block of time used to share this, which is where its eight-hour ceiling came from. A
 * block now asks when it ends instead, and is bounded by `MAX_ENTRY_DAYS`.
 */
export const ENTRY_MINUTES_BOUNDS: NumericBounds = { min: 5, max: 480, step: 5 };

/**
 * The longest anything on the diary may last, in days: any holiday or sick leave as one block.
 *
 * Mirrors `TimeSpan.MAX_LENGTH` in the API, which is also a CHECK in changelog 037. It is not
 * a preference: the diary read finds an entry that began before the day it draws by looking
 * back exactly this far, so a longer one would vanish from its own later days.
 */
export const MAX_ENTRY_DAYS = 31;

/**
 * What a therapist keeps of what the clinic bills them out at.
 *
 * Zero is deliberately allowed: a therapist on a salary who keeps none of the billing is a
 * real arrangement, and refusing it would leave the clinic writing it as 1%. The API's column
 * has two decimals so a 42.5 split can be allowed later, but the form offers whole percents
 * and the schema is `integerIn` -- widen both together or not at all.
 */
export const REVENUE_SHARE_PERCENT_BOUNDS: NumericBounds = { min: 0, max: 100 };

/**
 * How faint the clinic's mark is printed behind an evaluation document, as a percentage.
 *
 * The floor is one rather than nought: an invisible mark is what *removing* the image
 * means, and it should not also be reachable by dragging a slider to the bottom. The
 * ceiling is where a full-page mark stops being a background and starts competing with the
 * 9.5pt text it sits behind.
 *
 * Mirrors `TenantBrandingRequest.MIN/MAX_WATERMARK_OPACITY` in the backend, which is also a
 * CHECK constraint in changelog 019 -- three statements of one range, and the API refuses
 * anything this lets through.
 */
export const WATERMARK_OPACITY_BOUNDS: NumericBounds = { min: 1, max: 50 };

/** How wide the mark is drawn, as a percentage of the page. Below 20 it is not a mark. */
export const WATERMARK_SCALE_BOUNDS: NumericBounds = { min: 20, max: 100 };

/**
 * How many rooms of one kind a clinic has. Nought is what removing the room means, not a
 * room nobody can be booked into. Mirrors `RoomRequest` in the API and the CHECK in its
 * changelog 047.
 */
export const ROOM_CAPACITY_BOUNDS: NumericBounds = { min: 1, max: 50 };

/** The browser's own bounds, from the same declaration the Yup rule reads. */
export const numericSlotProps = (bounds: NumericBounds): { htmlInput: { min: number; max: number; step: number } } =>
    ({ htmlInput: { min: bounds.min, max: bounds.max, step: bounds.step ?? 1 } });

/**
 * A whole number within bounds, held as a string.
 *
 * Numbers stay strings in form values because that is what an input gives back, and
 * coercing one per keystroke is what makes a field impossible to clear. Blank passes
 * unless `required`; the model converts once on submit.
 */
export const integerIn = (
    bounds: NumericBounds,
    translate: TranslateFn,
    invalidKey: string,
    { required = false, requiredKey = invalidKey }: { required?: boolean; requiredKey?: string } = {}
): yup.StringSchema<string> =>
    yup.string().defined()
        .test('present', translate(requiredKey, { ...bounds }), (value) => !required || !!value?.trim())
        .test('range', translate(invalidKey, { ...bounds }), (value) => {
            if (!value?.trim()) {
                return !required;
            }

            const parsed: number = Number(value);
            return Number.isInteger(parsed) && parsed >= bounds.min && parsed <= bounds.max;
        });
